import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { format } from "date-fns";
import { Coins, TrendingUp, TrendingDown, Calendar, ChevronLeft, ChevronRight } from "lucide-react";

interface CreditTransaction {
  id: string;
  npo_id: string;
  amount: number;
  transaction_type: string;
  description: string;
  created_at: string;
  previous_balance: number;
  new_balance: number;
}

interface Pagination {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface CreditsResponse {
  data: {
    transactions: CreditTransaction[];
    pagination: Pagination;
  };
  message: string;
}

const fetchCredits = async (token: string, page: number, limit: number) => {
  const response = await fetch(
    `${import.meta.env.VITE_CC_API_URL}/v1/me/credits?page=${page}&limit=${limit}`, 
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  
  if (!response.ok) {
    throw new Error('Failed to fetch credit transactions');
  }
  
  return response.json();
};

const CreditsPage = ({ session, profile }: { session: any; profile: any }) => {
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(20);
  const [activeTab, setActiveTab] = useState<string>("all");
  const [currentBalance, setCurrentBalance] = useState<number>(0);

  const { data, isLoading, error } = useQuery<CreditsResponse>({
    queryKey: ["credits", page, limit],
    queryFn: () => fetchCredits(session?.access_token, page, limit),
    enabled: !!session?.access_token,
  });

  useEffect(() => {
    if (profile?.credits !== undefined) {
      setCurrentBalance(profile.credits);
    } else if (data?.data?.transactions && data.data.transactions.length > 0) {
      // If profile credits not available, use the most recent transaction's new_balance
      setCurrentBalance(data.data.transactions[0].new_balance);
    }
  }, [profile, data]);

  const filteredTransactions = data?.data?.transactions?.filter((transaction) => {
    if (activeTab === "all") return true;
    return transaction.transaction_type === activeTab;
  }) || [];

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), "MMM dd, yyyy • h:mm a");
    } catch (error) {
      return dateString;
    }
  };

  const getTransactionIcon = (type: string) => {
    switch (type) {
      case "top_up":
        return <TrendingUp className="h-4 w-4 text-green-500" />;
      case "deduct":
        return <TrendingDown className="h-4 w-4 text-amber-500" />;
      default:
        return <Coins className="h-4 w-4 text-gray-500" />;
    }
  };

  const getTransactionBadge = (type: string) => {
    switch (type) {
      case "top_up":
        return (
          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
            Credit Added
          </Badge>
        );
      case "deduct":
        return (
          <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
            Credit Used
          </Badge>
        );
      default:
        return (
          <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200">
            {type}
          </Badge>
        );
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-[200px] w-full rounded-lg" />
        <Skeleton className="h-[400px] w-full rounded-lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto py-8">
        <Card>
          <CardHeader>
            <CardTitle>Error Loading Credits</CardTitle>
            <CardDescription>
              There was an error loading your credit information. Please try again later.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-2xl font-bold">Credits Dashboard</CardTitle>
          <CardDescription>
            View your credit balance and transaction history
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
              <CardHeader className="pb-2">
                <CardDescription className="text-amber-700 font-medium">Current Balance</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2">
                  <Coins className="h-6 w-6 text-amber-500" />
                  <span className="text-3xl font-bold text-amber-700">{currentBalance}</span>
                  <span className="text-amber-700 font-medium">Credits</span>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
              <CardHeader className="pb-2">
                <CardDescription className="text-green-700 font-medium">Monthly Credits</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2">
                  <Calendar className="h-6 w-6 text-green-500" />
                  <span className="text-3xl font-bold text-green-700">
                    {data?.data?.transactions?.filter(t => 
                      t.transaction_type === "top_up" && 
                      t.description.includes("Monthly subscription")
                    )[0]?.amount || 300}
                  </span>
                  <span className="text-green-700 font-medium">per month</span>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
              <CardHeader className="pb-2">
                <CardDescription className="text-blue-700 font-medium">Total Transactions</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-6 w-6 text-blue-500" />
                  <span className="text-3xl font-bold text-blue-700">{data?.data?.pagination?.total || 0}</span>
                  <span className="text-blue-700 font-medium">transactions</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>

      {/* Subscription Banner */}
      <Card className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white col-span-1 md:col-span-3 my-4">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="mb-4 md:mb-0">
              <h3 className="text-xl font-bold mb-2">Unlock Full Benefits</h3>
              <p className="text-blue-100">Subscribe now to access premium features and unlimited credits.</p>
            </div>
            <Button 
              className="bg-white text-blue-700 hover:bg-blue-50" 
              onClick={() => window.location.href = '/dashboard/subscription'}
            >
              Subscribe Now
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Transaction History</CardTitle>
          <Tabs defaultValue="all" value={activeTab} onValueChange={setActiveTab}>
            <TabsList>
              <TabsTrigger value="all">All Transactions</TabsTrigger>
              <TabsTrigger value="top_up">Credits Added</TabsTrigger>
              <TabsTrigger value="deduct">Credits Used</TabsTrigger>
            </TabsList>
          </Tabs>
        </CardHeader>
        <CardContent>
          {filteredTransactions && filteredTransactions.length > 0 ? (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead className="text-right">Balance</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTransactions.map((transaction) => (
                    <TableRow key={transaction.id}>
                      <TableCell className="font-medium">
                        {formatDate(transaction.created_at)}
                      </TableCell>
                      <TableCell>{transaction.description}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {getTransactionIcon(transaction.transaction_type)}
                          {getTransactionBadge(transaction.transaction_type)}
                        </div>
                      </TableCell>
                      <TableCell className={`text-right font-medium ${
                        transaction.transaction_type === "top_up" 
                          ? "text-green-600" 
                          : "text-amber-600"
                      }`}>
                        {transaction.transaction_type === "top_up" ? "+" : "-"}
                        {transaction.amount}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {transaction.new_balance}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              No transactions found.
            </div>
          )}
        </CardContent>
        {data?.data?.pagination && (
          <div className="px-6 py-4 flex items-center justify-between border-t">
            <div className="text-sm text-gray-500">
              Showing page {data.data.pagination.page} of {data.data.pagination.totalPages}
            </div>
            <div className="flex items-center space-x-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page <= 1}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setPage(p => p + 1)}
                disabled={!data.data.pagination || page >= data.data.pagination.totalPages}
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-500">Items per page:</span>
              <Select value={limit.toString()} onValueChange={(value) => {
                setLimit(parseInt(value));
                setPage(1); // Reset to first page when changing limit
              }}>
                <SelectTrigger className="w-16">
                  <SelectValue placeholder="20" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default CreditsPage;
